"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Graduate } from "@/types/graduate";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GraduateDetailsModal from "@/components/GraduateDetailsModal";

export default function WallOfFamePage() {
  const [graduates, setGraduates] = useState<Graduate[]>([]);
  const [imageDims, setImageDims] = useState<
    Record<string, { w: number; h: number }>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGraduate, setSelectedGraduate] = useState<Graduate | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchGraduates();
  }, []);

  const fetchGraduates = async () => {
    try {
      setIsLoading(true);
      const response = await fetch("/api/graduates");

      if (!response.ok) {
        throw new Error("Failed to fetch graduates");
      }

      const data: Graduate[] = await response.json();

      // Preload every photo and record its natural dimensions before
      // rendering, so each card reserves the exact height up front and the
      // layout never shifts while images stream in.
      const dims: Record<string, { w: number; h: number }> = {};
      await Promise.all(
        data
          .filter((g) => g.image)
          .map(
            (g) =>
              new Promise<void>((resolve) => {
                const img = new window.Image();
                img.onload = () => {
                  dims[g.id] = { w: img.naturalWidth, h: img.naturalHeight };
                  resolve();
                };
                img.onerror = () => {
                  dims[g.id] = { w: 600, h: 450 };
                  resolve();
                };
                img.src = g.image!;
              })
          )
      );

      setImageDims(dims);
      setGraduates(data);
    } catch (err) {
      console.error("Error fetching graduates:", err);
      setError("Failed to load graduates");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGraduateClick = (graduate: Graduate) => {
    setSelectedGraduate(graduate);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedGraduate(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 pt-16 pb-8 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading Wall of Fame...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 pt-16 pb-8 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchGraduates}
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-14 sm:pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back button */}
          <div className="mb-8">
            <Link
              href="/#about"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 hover:text-accent hover:border-accent transition-all duration-200 group"
            >
              <svg
                className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Wall of Fame
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Celebrating our graduated students who have successfully completed
              their helicopter training at TopCat Helicopters.
            </p>
          </div>

          {/* Graduates Grid */}
          {graduates.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Graduates Yet
              </h3>
              <p className="text-gray-600">
                Our Wall of Fame will showcase graduates as they complete their
                training.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
              {graduates.map((graduate) => (
                <div
                  key={graduate.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-accent cursor-pointer transform hover:-translate-y-1 flex flex-col overflow-hidden"
                  onClick={() => handleGraduateClick(graduate)}
                >
                  {/* Graduate Photo - Natural Aspect Ratio */}
                  <div className="w-full bg-gray-100">
                    {graduate.image ? (
                      <Image
                        src={graduate.image}
                        alt={graduate.name}
                        width={imageDims[graduate.id]?.w ?? 600}
                        height={imageDims[graduate.id]?.h ?? 450}
                        unoptimized
                        className="w-full h-auto"
                      />
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center">
                        <svg
                          className="w-12 h-12 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Graduate Info - Consistent Position */}
                  <div className="text-center px-6 pt-5 pb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {graduate.name}
                    </h3>
                    <p className="text-accent font-medium mb-1">
                      {graduate.helicopter_type}
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      Graduated:{" "}
                      {new Date(graduate.graduation_date).toLocaleDateString(
                        "en-US",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }
                      )}
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {graduate.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      <GraduateDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        graduate={selectedGraduate}
      />
    </div>
  );
}
